import { Close, Feedback } from '@mui/icons-material';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Popover,
  TextField,
  type PopoverProps,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Button, type ButtonType } from '~/components/Button';
import EmailInput from '~/components/EmailInput';
import { useFeedback } from '~/hooks/useFeedback';
import { useUser } from '~/hooks/useUser';
import { useHandleException } from '~/scripts/handleException';

interface FeedbackButtonProps extends Omit<ButtonType, 'onClick'> {
  onClick?: () => void;
  PopoverProps?: PopoverProps;
  iconOnly?: boolean;
}
export const FeedbackButton = ({
  PopoverProps,
  iconOnly,
  ...buttonProps
}: FeedbackButtonProps) => {
  const user = useUser();
  const [email, setEmail] = useState(user?.email || '');
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const handleException = useHandleException();
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user?.email]);
  const {
    feedback,
    setFeedback,
    submit,
    success,
    error,
    reset,
    loading,
    disabled,
  } = useFeedback(email);

  const { anchorOrigin, ...popoverProps } = PopoverProps || {};

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (error) {
      handleException(error);
      reset();
    }
  }, [error, handleException, reset]);

  return (
    <>
      {iconOnly ? (
        <IconButton
          style={{ color: 'white' }}
          onClick={() => setOpen(true)}
          id="feedback-button"
          ref={anchorRef}
          aria-describedby={'feedback-modal'}
          {...buttonProps}>
          <Feedback fontSize="small" />
        </IconButton>
      ) : (
        <Button
          id="feedback-button"
          aria-describedby={'feedback-modal'}
          variant="text"
          size="small"
          color="secondary"
          ref={anchorRef}
          startIcon={<Feedback fontSize="small" />}
          onClick={() => setOpen(true)}
          {...buttonProps}>
          Share Feedback
        </Button>
      )}
      <Popover
        id={'feedback-modal'}
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
          ...anchorOrigin,
        }}
        {...popoverProps}>
        <Card>
          <IconButton
            onClick={() => onClose()}
            sx={{ position: 'absolute', right: 10, top: 10 }}>
            <Close />
          </IconButton>
          {!success && (
            <form
              onSubmit={(event) => {
                event?.preventDefault();
                submit(feedback);
              }}>
              <CardHeader title="Share Feedback" />
              <CardContent sx={{ pt: 0 }}>
                <EmailInput
                  value={email}
                  setValue={setEmail}
                  size="small"
                  disabled={!!user?.email}
                  autoFocus
                  required
                  fullWidth
                />
                <TextField
                  multiline
                  fullWidth
                  margin="normal"
                  minRows={4}
                  required
                  error={!!feedback && feedback.length < 10}
                  label="Feedback"
                  size="small"
                  placeholder="Enter your feedback here..."
                  value={feedback}
                  onChange={(event) => setFeedback(event.target.value)}
                />
              </CardContent>
              <Divider />
              <CardActions>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={disabled}
                  loading={loading}
                  fullWidth>
                  Submit Feedback
                </Button>
              </CardActions>
            </form>
          )}
          {success && (
            <CardHeader
              title="🏆 Thank you for your feedback!"
              sx={{ pr: '80px' }}
            />
          )}
        </Card>
      </Popover>
    </>
  );
};
